// kan-111 AI Reference Taker — status trail popup. Shared by Settings › AI
// References and the candidate References tab (AI Reference Taker table).
// Server rule: requester, Executive/Admin, or the candidate's owner / HR.
import { useLazyQuery } from '@apollo/client';
import { useEffect } from 'react';
import { Button, Modal, ModalBody, ModalFooter } from 'reactstrap';
import CLOSE_ICON from '../../../assets/images/close-icon.svg';
import Loader from '../../../components/layout/Loader';
import { AI_REFERENCE_TRAIL, AiReferenceTrailRow } from './gql';
import { NEEDS_HUMAN_REASON_LABEL, formatDateTime, statusLabel } from './utils';

/** Anything with the entry id + a display name (settings row or candidate row). */
export type AiReferenceTrailTarget = {
  entryId?: string | null;
  referenceName?: string | null;
};

const AiReferenceTrailModal = ({
  row,
  toggle,
}: {
  row: AiReferenceTrailTarget | null;
  toggle: () => void;
}) => {
  const [getTrail, { data, loading, error }] = useLazyQuery<{
    aiReferenceTrail: AiReferenceTrailRow[];
  }>(AI_REFERENCE_TRAIL, { fetchPolicy: 'network-only' });

  useEffect(() => {
    if (row?.entryId) {
      getTrail({ variables: { referenceEntryId: row.entryId } });
    }
    // eslint-disable-next-line
  }, [row?.entryId]);

  const trail = data?.aiReferenceTrail ?? [];

  return (
    <Modal
      isOpen={!!row}
      toggle={toggle}
      className="revampDialog revampDialogWidth"
      scrollable
    >
      <div className="modalHeaderCustom d-flex align-items-center justify-content-between">
        <h5 className="m-0">Status Trail — {row?.referenceName ?? 'Reference'}</h5>
        <Button className="p-0 bg-transparent border-0" onClick={toggle}>
          <img src={CLOSE_ICON} alt="close" />
        </Button>
      </div>

      <ModalBody className="modalBodyScroll">
        {loading && <Loader loaderClass="sm-height" />}
        {!loading && error && (
          <p className="text-danger">Could not load the trail: {error.message}</p>
        )}
        {!loading && !error && !trail.length && (
          <p className="text-muted mb-0">No status history yet.</p>
        )}
        {!loading && !!trail.length && (
          <ul className="list-unstyled mb-0">
            {trail.map((item) => (
              <li key={item.id} className="mb-3 pb-2 border-bottom">
                <div>
                  <strong>
                    {item.fromStatus ? `${statusLabel(item.fromStatus)} → ` : ''}
                    {statusLabel(item.toStatus)}
                  </strong>
                </div>
                <small className="text-muted d-block">
                  {formatDateTime(item.createdAt)} · {item.actorName || 'System'}
                </small>
                {item.reason && (
                  <small className="d-block">
                    Reason: {NEEDS_HUMAN_REASON_LABEL[item.reason] ?? item.reason}
                  </small>
                )}
                {item.detail && <small className="d-block">{item.detail}</small>}
              </li>
            ))}
          </ul>
        )}
      </ModalBody>

      <ModalFooter className="pt-3">
        <button type="button" onClick={toggle} className="buttonPrimary">
          Close
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default AiReferenceTrailModal;
